import { memo, useEffect, useState } from 'react';

import { Avatar, Flex, Form, Input, Typography } from 'antd';

import { UserOutlined } from '@ant-design/icons';
import equal from 'fast-deep-equal';

import { PASSWORD_PLACEHOLDER } from '../../../constants/common';
import messages from '../../../constants/messages';

import ImageUploader from '../../../components/ImageUploader';

import styles from './styles.module.scss';

const { Title } = Typography;

function ProfileCard({
  avatarUrl,
  editMode,
  file,
  formRef,
  user,
  handleChange,
  onChange,
  onUserEditChange,
  setSubmittable
}) {
  const [form] = Form.useForm();
  const [initialFormValues, setInitialFormValues] = useState({});

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  useEffect(() => {
    setInitialFormValues(form.getFieldsValue(true));
  }, []);

  const handleValuesChange = changedValues => {
    const [[changedKey, changedValue]] = Object.entries(changedValues);

    markFormDirty();
    onUserEditChange(changedValue, changedKey);
  };

  function markFormDirty() {
    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
  }

  return (
    <div className="w-full mt-4">
      <Flex vertical={true} align="center">
        <Avatar
          className="mb-2"
          crossOrigin="anonymous"
          icon={<UserOutlined />}
          size={{ xs: 150, sm: 200, md: 200, lg: 130, xl: 150, xxl: 200 }}
          src={file || avatarUrl}
        />
        {editMode && (
          <ImageUploader
            className="mb-2"
            cropShape="round"
            file={file}
            url={avatarUrl}
            editMode={editMode}
            handleChange={handleChange}
          />
        )}

        {user?.first_name && user?.last_name && (
          <Title level={5} className="mt-2 mb-4">
            {`${user?.first_name} ${user?.last_name}`}
          </Title>
        )}
      </Flex>

      <Form layout="vertical" form={form} onValuesChange={handleValuesChange}>
        <Flex vertical={true} className="w-full" align="start" gap="small">
          <Title level={5}>Manage Password</Title>

          <Form.Item
            label="Old Password"
            name="old_password"
            className={styles['form-item']}
            dependencies={['password']}
            rules={[
              {
                required: editMode && form.isFieldTouched('password'),
                message: messages.FIELD_REQUIRED('Old Password')
              }
            ]}>
            <Input.Password disabled={!editMode} placeholder={PASSWORD_PLACEHOLDER} />
          </Form.Item>
          <Form.Item label="New Password" name="password" className={styles['form-item']}>
            <Input.Password disabled={!editMode} placeholder={PASSWORD_PLACEHOLDER} />
          </Form.Item>
          <Form.Item
            label="Confirm New Password"
            name="password_confirm"
            className={styles['form-item']}
            dependencies={['password']}
            rules={[
              {
                required: editMode && form.isFieldTouched('password'),
                message: messages.FIELD_REQUIRED('Confirm New Password')
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(messages.PASSWORDS_MISMATCH));
                }
              })
            ]}>
            <Input.Password disabled={!editMode} placeholder={PASSWORD_PLACEHOLDER} />
          </Form.Item>
        </Flex>
      </Form>
    </div>
  );
}

export default memo(ProfileCard);
